import { useEffect, useState } from 'react';

import refreshFinancingContent from 'commons/refreshFinancingContent';
import useAffirmScript from 'hooks/useAffirmScript';
import poll from 'commons/poll';

// The Affirm script injects link elenents that show the Affirm modal when
// clicked. The default Affirm links include extra information that is not
// needed in the Consumer app, so we normally hide the default Affirm link and
// use a custom button to trigger the Affirm modal instead.

// A ref to the wrapper element where the default link is injected should be
// passed into this hook
const useAffirmRefreshHTML = defaultLinkWrapperRef => {
  const [isDefaultLinkLoaded, setIsDefaultLinkLoaded] = useState(false);

  // 1) Load Affirm script if not already loaded
  useAffirmScript({
    onLoad: refreshFinancingContent,
  });

  // 2) Once Affirm script is loaded, call refreshFinancingContent to trigger
  //    Affirm to inject the default Affirm links
  useEffect(() => {
    if (!defaultLinkWrapperRef) {
      return null;
    }

    const clearTimer = poll({
      condition: () => {
        const isAffirmScriptReady = !!(
          window?.affirm?.ui?.ready &&
          window?.affirm?.ui?.refresh &&
          defaultLinkWrapperRef?.current
        );

        return isAffirmScriptReady;
      },
      callback: refreshFinancingContent,
      interval: 250, // Try every 250ms, until it is ready
      maxRetries: 80, // Try a maximum of 80 times (timeout after 20 secs)
    });

    return clearTimer;
  }, [defaultLinkWrapperRef]);

  // 3) Check if the default Affirm link has been added within the specified
  //    hidden element wrapper. Once it has, we should return true from this
  //    hook so that we can show a custom button (e.g. "Learn More" on PDP).
  useEffect(() => {
    if (!defaultLinkWrapperRef) {
      return null;
    }

    const clearTimer = poll({
      condition: () => {
        const affirmWrapper = defaultLinkWrapperRef?.current;

        // Find first link or button in affirm HTML (we check buttons too in
        // case Affirm change to a button in the future)
        const affirmLinkOrButton = affirmWrapper?.querySelector?.('a,button');

        return !!affirmLinkOrButton;
      },
      callback: () => {
        setIsDefaultLinkLoaded?.(true);
      },
      interval: 250, // Try every 250ms, until it is ready
      maxRetries: 80, // Try a maximum of 80 times (timeout after 20 secs)
    });

    return clearTimer;
  }, [defaultLinkWrapperRef]);

  return isDefaultLinkLoaded;
};

export default useAffirmRefreshHTML;
